import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import {
  Alert,
  Badge,
  Collapse,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { ChevronDown, ChevronUp, Copy, RefreshCw } from 'react-feather';
import { QRCodeSVG } from 'qrcode.react';
import { useDispatch } from 'react-redux';

import classroomAttendanceApi from 'api/classroomAttendanceApi';
import { SOCKET_NAMESPACE } from 'constants/constants';
import { setQrAttendanceCount } from 'store/classroomAttendance';
import { useQrAttendanceCount } from 'hooks/classroomAttendance';
import { useSocket } from 'hooks/useSocket';
import { useClipboard } from 'hooks/use-clipboard';
import { showToastSuccess } from '@core/components/toastify/ToastTypes';
import ThemeButton from 'components/AntdFormInputs/theme-button';
import Loading from 'components/Loading/Loading';
import InputField from 'components/Form/Input/input-field';

const getErrorMessage = (error) => {
  if (error?.response?.data?.errors?.[0]?.msg) {
    return error.response.data.errors[0].msg;
  }
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }
  if (typeof error?.response?.data === 'string') {
    return error.response.data;
  }
  return error?.message || 'Unable to generate QR attendance code.';
};

const hasLocationCoordinates = (session) => {
  const coordinates = session?.locationMaster?.coordinates;
  const latitude = Number(coordinates?.latitude);
  const longitude = Number(coordinates?.longitude);

  return Number.isFinite(latitude) && Number.isFinite(longitude);
};

const formatCountdown = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(
    remainingSeconds,
  ).padStart(2, '0')}`;
};

const QR_ATTENDANCE_ACTION = {
  CHECK_IN: 'CHECK_IN',
  CHECK_OUT: 'CHECK_OUT',
};

const QR_ATTENDANCE_ACTION_LABEL = {
  [QR_ATTENDANCE_ACTION.CHECK_IN]: 'Check-in',
  [QR_ATTENDANCE_ACTION.CHECK_OUT]: 'Check-out',
};

const QR_ATTENDANCE_ACTION_OPTIONS = [
  {
    value: QR_ATTENDANCE_ACTION.CHECK_IN,
    label: 'Check-in - record arrival',
  },
  {
    value: QR_ATTENDANCE_ACTION.CHECK_OUT,
    label: 'Check-out - record leaving',
  },
];

const DEFAULT_RADIUS_METERS = 100;
const DEFAULT_ATTENDANCE_COUNT = {
  totalAssigned: 0,
  checkedIn: 0,
  checkedOut: 0,
  pending: 0,
};

const normalizeScanUrl = (payload) => {
  if (!payload?.scanUrl && !payload?.token) return '';
  const scanUrl = payload?.scanUrl || `/attendance/qr/${payload.token}`;

  if (/^https?:\/\//i.test(scanUrl)) {
    return scanUrl;
  }

  if (typeof window === 'undefined') {
    return scanUrl;
  }

  return scanUrl.startsWith('/')
    ? `${window.location.origin}${scanUrl}`
    : `${window.location.origin}/${scanUrl}`;
};

const AttendanceQrModal = ({ open, toggle, session, unitId }) => {
  const modalBodyRef = useRef();
  const dispatch = useDispatch();
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [now, setNow] = useState(Date.now());
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [attendanceAction, setAttendanceAction] = useState(
    QR_ATTENDANCE_ACTION.CHECK_IN,
  );
  const [radiusMeters, setRadiusMeters] = useState(
    String(DEFAULT_RADIUS_METERS),
  );
  const { copied, clipboardCopy } = useClipboard();
  const attendanceCount =
    useQrAttendanceCount(session?._id) || DEFAULT_ATTENDANCE_COUNT;
  const socketParams = useMemo(
    () => ({
      session: session?._id,
    }),
    [session?._id],
  );
  const socketNamespace =
    open && qrData?.scanUrl && session?._id
      ? SOCKET_NAMESPACE.CLASSROOM_ATTENDANCE
      : '';

  useSocket(socketNamespace, socketParams);

  const hasCoordinates = useMemo(
    () => hasLocationCoordinates(session),
    [session],
  );
  const radiusValue = Number(radiusMeters);
  const hasValidRadius = Boolean(
    Number.isInteger(radiusValue) && radiusValue > 0 && radiusValue <= 2000,
  );
  const selectedAttendanceActionOption = QR_ATTENDANCE_ACTION_OPTIONS.find(
    (option) => option.value === attendanceAction,
  );
  const liveCountAction = qrData?.attendanceAction || attendanceAction;
  const liveCountActionLabel =
    QR_ATTENDANCE_ACTION_LABEL[liveCountAction] || 'Attendance';

  const expiresAt = qrData?.expiresAt
    ? new Date(qrData.expiresAt).getTime()
    : null;
  const secondsLeft = expiresAt
    ? Math.max(0, Math.ceil((expiresAt - now) / 1000))
    : 0;
  const isExpired = Boolean(expiresAt && secondsLeft <= 0);

  const generateQrToken = async () => {
    if (!session?._id || !unitId || !hasCoordinates) {
      return;
    }

    if (!hasValidRadius) {
      setErrorMessage('Allowed scan distance must be from 1 to 2000 meters.');
      return;
    }

    try {
      setLoading(true);
      setErrorMessage('');
      const response = await classroomAttendanceApi.createQrToken({
        sessionId: session._id,
        unitId,
        attendanceAction,
        radiusMeters: radiusValue,
      });

      setQrData({
        ...response,
        scanUrl: normalizeScanUrl(response),
      });
      if (response?.attendanceCount) {
        dispatch(
          setQrAttendanceCount({
            sessionId: session._id,
            unitId,
            attendanceAction: response.attendanceAction || attendanceAction,
            attendanceCount: response.attendanceCount,
            updatedAt: Date.now(),
          }),
        );
      }
      setSettingsOpen(false);
      setNow(Date.now());
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!qrData?.scanUrl) return;
    await clipboardCopy(qrData.scanUrl);
    showToastSuccess('Scan link copied');
  };

  const handleActionChange = (option) => {
    setAttendanceAction(option?.value || QR_ATTENDANCE_ACTION.CHECK_IN);
    setQrData(null);
    setErrorMessage('');
    setSettingsOpen(true);
  };

  const handleRadiusChange = (event) => {
    setRadiusMeters(event.target.value);
    setQrData(null);
    setErrorMessage('');
    setSettingsOpen(true);
  };

  useEffect(() => {
    if (!open) {
      setQrData(null);
      setErrorMessage('');
      setSettingsOpen(true);
      return;
    }

    setQrData(null);
    setErrorMessage('');
    setSettingsOpen(true);
    setNow(Date.now());
  }, [open, hasCoordinates, session?._id, unitId]);

  useEffect(() => {
    if (!open) return undefined;

    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, [open]);

  return (
    <Modal
      isOpen={open}
      toggle={toggle}
      className="modal-dialog-centered"
      contentClassName="p-0"
    >
      <ModalHeader toggle={toggle}>
        <span className="sub-heading_text_title">QR Attendance</span>
      </ModalHeader>
      <ModalBody
        style={{ maxHeight: '72vh', overflowY: 'auto' }}
        ref={modalBodyRef}
      >
        <div className="text-center mb-1">
          <h5 className="mb-25">{qrData?.sessionName || session?.name}</h5>
          {!qrData?.scanUrl && (
            <small className="text-muted">
              Learners scan with their phone camera.
            </small>
          )}
        </div>

        {!hasCoordinates && (
          <Alert color="warning" className="mb-0">
            Location coordinates are required before QR attendance can be
            generated. Update this classroom session with a Location Master that
            has latitude and longitude.
          </Alert>
        )}

        {hasCoordinates && (
          <div className="border rounded mb-1">
            <button
              type="button"
              className="w-100 bg-white border-0 p-1 text-left d-flex align-items-center justify-content-between"
              onClick={() => setSettingsOpen(!settingsOpen)}
              aria-expanded={settingsOpen}
            >
              <span>
                <span className="font-weight-bold d-block">QR setup</span>
                <small className="text-muted">
                  {QR_ATTENDANCE_ACTION_LABEL[attendanceAction]}
                  {' | '}
                  {hasValidRadius ? radiusValue : DEFAULT_RADIUS_METERS}
                  {' meters'}
                </small>
              </span>
              {settingsOpen ? (
                <ChevronUp size={18} />
              ) : (
                <ChevronDown size={18} />
              )}
            </button>

            <Collapse isOpen={settingsOpen}>
              <div className="px-1 pb-1">
                <div className="form-row">
                  <FormGroup className="col-md-7 mb-1 mb-md-0">
                    <Label for="qrAttendanceAction">QR purpose</Label>
                    <Select
                      inputId="qrAttendanceAction"
                      instanceId="qrAttendanceAction"
                      className="react-select"
                      classNamePrefix="react-select"
                      value={selectedAttendanceActionOption}
                      options={QR_ATTENDANCE_ACTION_OPTIONS}
                      onChange={handleActionChange}
                      isDisabled={loading}
                      isClearable={false}
                    />
                  </FormGroup>

                  <FormGroup className="col-md-5 mb-0">
                    <Label for="qrAttendanceRadius">Distance (meters)</Label>
                    <InputField
                      id="qrAttendanceRadius"
                      type="number"
                      min="1"
                      max="2000"
                      step="1"
                      value={radiusMeters}
                      onChange={handleRadiusChange}
                      disabled={loading}
                      placeholder="Example: 100"
                    />
                  </FormGroup>
                </div>

                <small className="text-muted">
                  Scans must be within this distance from the classroom.
                </small>
              </div>
            </Collapse>
          </div>
        )}

        {errorMessage && (
          <Alert color="danger" className="mb-1">
            {errorMessage}
          </Alert>
        )}

        {hasCoordinates && (
          <div className="d-flex flex-column align-items-center">
            {loading ? (
              <div className="py-3">
                <Loading />
              </div>
            ) : (
              qrData?.scanUrl && (
                <div className="border rounded p-1 w-100">
                  <div
                    style={{ gap: 10 }}
                    className="d-flex flex-column align-items-center justify-content-center"
                  >
                    <div className="d-flex flex-column align-items-center">
                      <div
                        className="p-50 bg-white border rounded"
                        style={{ lineHeight: 0 }}
                      >
                        <QRCodeSVG
                          value={qrData.scanUrl}
                          size={190}
                          level="M"
                          marginSize={4}
                          title="Classroom attendance QR code"
                        />
                      </div>
                      <div className="mt-75">
                        <Badge
                          color={isExpired ? 'light-danger' : 'light-info'}
                        >
                          {isExpired
                            ? 'Expired'
                            : `Expires in ${formatCountdown(secondsLeft)}`}
                        </Badge>
                      </div>
                    </div>

                    <div className="w-100 border-top p-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="font-weight-bold">Live count</div>
                        <Badge color="light-info">{liveCountActionLabel}</Badge>
                      </div>
                      <div className="d-flex justify-content-between text-center mt-75">
                        <div className="px-50">
                          <div className="font-weight-bold">
                            {attendanceCount.checkedIn || 0}
                          </div>
                          <small className="text-muted">Checked in</small>
                        </div>
                        <div className="px-50">
                          <div className="font-weight-bold">
                            {attendanceCount.checkedOut || 0}
                          </div>
                          <small className="text-muted">Checked out</small>
                        </div>
                        <div className="px-50">
                          <div className="font-weight-bold">
                            {attendanceCount.totalAssigned || 0}
                          </div>
                          <small className="text-muted">Assigned</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {hasCoordinates && qrData?.scanUrl && (
          <ThemeButton variant="cancel" onClick={handleCopy} padding="9px 10px">
            <Copy size={15} className="mr-50" />
            {copied ? 'Copied' : 'Copy Link'}
          </ThemeButton>
        )}
        {hasCoordinates && (
          <ThemeButton
            variant="primary"
            onClick={generateQrToken}
            disabled={loading || !hasValidRadius}
            padding="9px 10px"
          >
            <RefreshCw size={15} className="mr-50" />
            {qrData
              ? 'Refresh QR'
              : `Generate ${QR_ATTENDANCE_ACTION_LABEL[attendanceAction]} QR`}
          </ThemeButton>
        )}
      </ModalFooter>
    </Modal>
  );
};

AttendanceQrModal.propTypes = {
  open: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  session: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    locationMaster: PropTypes.shape({
      coordinates: PropTypes.shape({
        latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    }),
  }),
  unitId: PropTypes.string,
};

AttendanceQrModal.defaultProps = {
  session: undefined,
  unitId: '',
};

export default AttendanceQrModal;
